import { useMemo, useState } from 'react';
import type { OrganizationService } from '@/features/catalog/catalog';
import { leerRecientes, registrarReciente } from './recientes';

/**
 * Selector de honorario para una línea del trabajo.
 *
 * Arranca VACÍO a propósito. Preseleccionar el primero del orden alfabético
 * hace que una línea sin tocar parezca elegida, y ese es justo el error que no
 * se puede cometer con un valor económico.
 *
 * Con el honorario sin elegir muestra búsqueda y los usados recientemente.
 * Elegido, se colapsa al nombre con un botón para cambiarlo: en un celular no
 * conviene ocupar media pantalla con una lista que ya cumplió su función.
 */

interface Props {
  organizationId: string;
  services: OrganizationService[];
  /** `null` cuando la línea todavía no tiene honorario elegido. */
  value: string | null;
  /** Nombre guardado, para honorarios que ya no están en el tarifario. */
  fallbackName?: string | undefined;
  /**
   * Si esta línea es la que se acaba de agregar.
   *
   * Sólo esa arranca con la lista abierta. Antes, agregar tres honorarios de
   * una dejaba tres selectores desplegados a la vez —314 px cada uno— y había
   * que scrollear entre listas para elegir en la primera.
   */
  reciénAgregada?: boolean;
  onChange: (service: OrganizationService) => void;
}

export function ServicePicker({
  organizationId,
  services,
  value,
  fallbackName,
  reciénAgregada = true,
  onChange,
}: Props) {
  const [abierto, setAbierto] = useState(reciénAgregada);

  // Cuando esta línea deja de ser la última agregada, su lista se cierra sola.
  //
  // No alcanza con el valor inicial de `useState`: se lee al montar y nada
  // más, así que la primera línea se quedaba abierta al agregar la segunda y
  // terminaban tres listas desplegadas a la vez. Se ajusta durante el render,
  // que es el patrón que React recomienda para derivar estado de props.
  const [eraNueva, setEraNueva] = useState(reciénAgregada);
  if (reciénAgregada !== eraNueva) {
    setEraNueva(reciénAgregada);
    if (!reciénAgregada && value === null) setAbierto(false);
  }
  const [busqueda, setBusqueda] = useState('');

  const elegido = services.find((s) => s.id === value) ?? null;
  const nombreVisible = elegido?.name ?? (value === null ? null : (fallbackName ?? null));

  const recientes = useMemo(() => {
    const ids = leerRecientes(organizationId);
    return ids
      .map((id) => services.find((s) => s.id === id))
      .filter((s): s is OrganizationService => Boolean(s));
  }, [organizationId, services]);

  const filtrados = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    if (!q) return services;
    return services.filter((s) => s.name.toLowerCase().includes(q));
  }, [services, busqueda]);

  const elegir = (s: OrganizationService) => {
    registrarReciente(organizationId, s.id);
    setAbierto(false);
    setBusqueda('');
    onChange(s);
  };

  // Colapsado: ya hay honorario elegido
  if (nombreVisible !== null && !abierto) {
    return (
      <div className="flex items-center gap-2">
        <span className="min-w-0 flex-1 truncate font-medium" data-testid="item-service-name">
          {nombreVisible}
        </span>
        <button
          type="button"
          onClick={() => setAbierto(true)}
          className="min-h-11 shrink-0 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-3 text-sm"
          data-testid="item-service-change"
        >
          Cambiar
        </button>
      </div>
    );
  }

  if (nombreVisible === null && !abierto) {
    return (
      <button
        type="button"
        onClick={() => setAbierto(true)}
        className="min-h-11 w-full rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] border-dashed px-3 text-left text-sm text-[var(--tinta-suave)]"
        data-testid="item-service-elegir"
      >
        Elegir honorario
      </button>
    );
  }

  return (
    <div className="flex flex-col gap-2" data-testid="item-service-picker">
      <input
        type="search"
        value={busqueda}
        autoFocus={abierto}
        onChange={(e) => setBusqueda(e.target.value)}
        placeholder="Seleccionar honorario"
        aria-label="Buscar honorario"
        className="min-h-11 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-3 text-base"
        data-testid="item-service-search"
      />

      {busqueda.trim() === '' && recientes.length > 0 && (
        <div className="flex flex-col gap-1">
          <span className="text-xs text-[var(--tinta-tenue)]">Usados recientemente</span>
          <div className="flex flex-wrap gap-2">
            {recientes.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => elegir(s)}
                className="min-h-11 rounded-full border border-[var(--linea)] px-3 text-sm"
                data-testid="item-service-recent"
              >
                {s.name}
              </button>
            ))}
          </div>
        </div>
      )}

      <ul
        className="max-h-56 overflow-y-auto rounded-[var(--radius-control)] border border-[var(--linea-fuerte)]"
        data-testid="item-service-options"
      >
        {filtrados.length === 0 && (
          <li className="p-3 text-sm text-[var(--tinta-suave)]">
            No hay honorarios que coincidan.
          </li>
        )}
        {filtrados.map((s) => (
          <li key={s.id}>
            <button
              type="button"
              onClick={() => elegir(s)}
              className="flex min-h-11 w-full items-center justify-between gap-3 px-3 text-left text-sm"
              data-testid="item-service-option"
            >
              <span className="min-w-0 truncate">{s.name}</span>
              <span className="shrink-0 text-xs text-[var(--tinta-tenue)]">{s.kg_novillo} kg</span>
            </button>
          </li>
        ))}
      </ul>

      {abierto && (
        <button
          type="button"
          onClick={() => {
            setAbierto(false);
            setBusqueda('');
          }}
          className="min-h-11 text-sm underline opacity-70"
          data-testid="item-service-cerrar"
        >
          {nombreVisible === null ? 'Cerrar la lista' : 'Cancelar cambio'}
        </button>
      )}
    </div>
  );
}
